<template>
  <section v-if="pricingPlans.length" id="pricing" class="relative overflow-visible bg-slate-25 py-8 md:py-9 lg:py-20">
    <div class="absolute inset-x-0 top-0 -z-10 h-64 bg-gradient-to-b from-hilab/10 via-transparent to-transparent"></div>
    <div class="w-full mx-auto px-4 sm:px-8 lg:px-12 xl:px-[120px]">
      <div class="flex flex-col items-center gap-2 mb-12 md:mb-16">
        <!-- Badge: text-sm/Medium, #36393E -->
        <h2 v-if="badge || title" class="font-inter text-xs md:text-sm font-medium leading-5 text-[#36393E] text-center">
          {{ badge || title || 'LỰA CHỌN GÓI DỊCH VỤ' }}
        </h2>

        <!-- Title: display-xs/Bold, Gradient -->
        <h3 v-if="heroDescription" class="font-display text-base md:text-xl lg:text-2xl font-bold leading-6 md:leading-7 lg:leading-8 text-center max-w-[484px]">
          <span class="pricing-title-gradient">
            {{ heroDescription || 'BẢNG GIÁ LINH HOẠT CHO MỌI NHU CẦU' }}
          </span>
        </h3>
      </div>
      <!-- Mobile/Tablet Grid: All plans including Enterprise -->
      <div class="pricing-grid grid grid-cols-1 md:grid-cols-2 gap-[30px] justify-center overflow-visible pt-6 lg:hidden">
        <article v-for="plan in allPlans" :key="plan.name" class="w-full max-w-[420px] mx-auto h-full">
          <div
            :class="[
              'relative z-[1] w-full h-full pt-16 pb-6 gap-5 rounded-2xl flex flex-col justify-start items-center transition-all duration-200 scroll-scale-up hover:-translate-y-1 hover:shadow-lg',
              plan.badge
                ? 'bg-[linear-gradient(103.39deg,#FAF6FF_1.17%,#FEFCFF_100%)] shadow-[0_0_24px_0_rgba(186,18,248,0.12)] border border-[#892DDE] border-b-4 border-b-[#4A1878]'
                : 'bg-[linear-gradient(142.89deg,#FAFAFA_21.54%,#FFFFFF_77.64%)] shadow-[0_4px_4px_rgba(0,0,0,0.02)] border border-[rgba(229,229,229,0.8)] border-b-4 border-b-[#E5E5E5]',
            ]"
          >
            <!-- Card Header Tab -->
            <div :class="['pricing-card-tab', getTabClass(plan.name)]">
              <h3 class="font-inter text-sm font-bold text-white uppercase tracking-wide">
                {{ plan.name }}
              </h3>
            </div>

            <!-- Header Subtitle -->
            <div class="h-5 min-h-5 flex items-start justify-center px-2 mb-9 text-center">
              <p v-if="plan.subtitle" class="text-xs font-bold leading-5 text-[#64748B] max-w-[302px]">
                {{ plan.subtitle }}
              </p>
            </div>

            <!-- Pricing -->
            <div class="h-[200px] min-h-[200px] flex flex-col justify-start">
              <div class="text-center h-full flex flex-col justify-between">
                <!-- Yearly Price (Primary) -->
                <div v-if="hasYearly(plan)" class="flex flex-col justify-start items-center py-3">
                  <div class="flex items-center justify-center gap-3">
                    <span :class="['pricing-price-value', getTabClass(plan.name)]">{{ plan.priceYearly }}</span>
                    <div v-if="plan.priceYearlyCurrency || plan.priceYearlySuffix" class="flex flex-col items-start">
                      <span v-if="plan.priceYearlyCurrency" class="pricing-currency">{{ plan.priceYearlyCurrency }}</span>
                      <span v-if="plan.priceYearlySuffix" class="pricing-suffix">{{ plan.priceYearlySuffix }}</span>
                    </div>
                  </div>
                  <p class="mb-4 text-xs font-normal italic text-center align-middle text-[#5C636C]">
                    {{ getBillingText(plan) }}
                  </p>
                  <div class="h-8 min-h-8 flex items-center justify-center">
                    <p v-if="plan.yearlyNote" class="inline-block rounded-full px-4 py-1.5 text-xs font-semibold text-center text-[#2C8A3C] bg-[#D5FAD8] leading-5">
                      {{ plan.yearlyNote }}
                    </p>
                  </div>
                </div>
                <div v-else class="flex flex-col justify-start items-center py-3"></div>

                <!-- Monthly Price (Secondary) -->
                <div v-if="hasMonthly(plan)" class="flex flex-col justify-start items-center mt-3 mb-7">
                  <div class="flex items-center justify-center gap-3">
                    <span class="text-[14px] font-black leading-[120%] align-middle text-slate-700">{{ plan.priceMonthly }}</span>
                    <div v-if="plan.priceMonthlyCurrency || plan.priceMonthlySuffix" class="flex flex-col items-start">
                      <span v-if="plan.priceMonthlyCurrency" class="text-[8px] font-black leading-[120%] align-middle text-[#B9BFC9]">{{ plan.priceMonthlyCurrency }}</span>
                      <span v-if="plan.priceMonthlySuffix" class="text-[8px] font-black leading-[120%] align-middle text-[#B9BFC9]">{{ plan.priceMonthlySuffix }}</span>
                    </div>
                  </div>
                  <p class="mt-1 text-[10px] font-normal italic text-center align-middle leading-[14px] text-[#5C636C]">Thanh toán theo tháng</p>
                </div>
                <div v-else class="flex flex-col justify-start items-center mt-3 mb-7"></div>
              </div>
            </div>

            <!-- Description -->
            <div class="h-14 min-h-14 flex items-center justify-center px-2">
              <p v-if="plan.description" class="text-xs leading-relaxed text-slate-600 text-center">
                {{ plan.description }}
              </p>
            </div>

            <!-- Features -->
            <div class="flex-1 w-full px-8 mt-auto">
              <ul class="space-y-3">
                <li v-for="feature in plan.features" :key="feature" class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-[10px] h-[18px]" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.0731 1.31716C8.97666 2.00536 6.3039 3.8458 3.83946 6.99844L2.3829 5.38204C2.15886 5.12608 1.74282 5.12608 1.51866 5.38204L0.446576 6.58252C0.238496 6.82252 0.254576 7.1746 0.478616 7.38268L3.77526 10.5513C4.0473 10.8074 4.49538 10.7433 4.6875 10.4073C6.4479 7.22248 8.33634 4.85404 11.5371 2.13328C11.9211 1.79728 11.569 1.15708 11.0729 1.31728L11.0731 1.31716Z"
                        fill="#2C8A3C"
                      />
                    </svg>
                  </div>
                  <span class="text-xs text-slate-600 leading-relaxed">{{ feature }}</span>
                </li>
              </ul>
            </div>

            <!-- CTA Button -->
            <div class="w-full px-8">
              <a
                v-if="plan.cta_type !== 'secondary'"
                :href="primaryCtaUrl"
                :class="[
                  'group relative block w-full min-w-[100px] h-12 rounded-full py-2.5 px-5 text-center text-sm font-bold align-middle transition-all duration-300 overflow-hidden shadow-[0_4px_4px_0_rgba(0,0,0,0.04)]',
                  plan.badge
                    ? 'bg-[linear-gradient(98.1deg,#892DDE_3.22%,#4A1878_100%),radial-gradient(59%_368.75%_at_52.5%_117.5%,rgba(232,15,239,0.2)_0%,rgba(255,255,255,0)_100%)] text-white hover:opacity-90'
                    : 'pricing-button-normal text-[#8326D9] bg-white hover:bg-slate-50',
                ]"
              >
                <span class="relative z-10">Bắt đầu ngay</span>
              </a>
              <button
                v-else
                type="button"
                class="group relative block w-full rounded-xl border border-slate-300 bg-white py-3 px-4 text-center text-xs font-semibold text-slate-700 transition-all duration-300 hover:border-hilab/30 hover:bg-hilab/5 hover:text-hilab overflow-hidden"
                @click="handleSecondaryClick(plan)"
              >
                <span class="relative z-10">Liên hệ tư vấn</span>
                <div class="absolute inset-0 bg-gradient-to-r from-hilab/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
              </button>
            </div>
          </div>
        </article>
      </div>

      <!-- PC Grid: Regular plans only (no Enterprise) -->
      <div class="pricing-grid hidden lg:grid grid-cols-3 gap-9 overflow-visible pt-6">
        <article v-for="plan in regularPlans" :key="plan.name" class="w-full h-full">
          <div
            :class="[
              'relative z-[1] w-full h-full pt-16 pb-6 gap-5 rounded-2xl flex flex-col justify-start items-center transition-all duration-200 scroll-scale-up hover:-translate-y-1 hover:shadow-lg',
              plan.badge
                ? 'bg-[linear-gradient(103.39deg,#FAF6FF_1.17%,#FEFCFF_100%)] shadow-[0_0_24px_0_rgba(186,18,248,0.12)] border border-[#892DDE] border-b-4 border-b-[#4A1878]'
                : 'bg-[linear-gradient(142.89deg,#FAFAFA_21.54%,#FFFFFF_77.64%)] shadow-[0_4px_4px_rgba(0,0,0,0.02)] border border-[rgba(229,229,229,0.8)] border-b-4 border-b-[#E5E5E5]',
            ]"
          >
            <!-- Card Header Tab -->
            <div :class="['pricing-card-tab', getTabClass(plan.name)]">
              <h3 class="font-inter text-sm font-bold text-white uppercase tracking-wide">
                {{ plan.name }}
              </h3>
            </div>

            <!-- Header Subtitle -->
            <div class="h-5 min-h-5 flex items-start justify-center px-2 mb-9 text-center">
              <p v-if="plan.subtitle" class="text-xs lg:text-sm font-bold leading-5 text-[#64748B] max-w-[302px] lg:max-w-none">
                {{ plan.subtitle }}
              </p>
            </div>

            <!-- Pricing -->
            <div class="h-[200px] min-h-[200px] flex flex-col justify-start">
              <div class="text-center h-full flex flex-col justify-between">
                <!-- Yearly Price (Primary) -->
                <div v-if="hasYearly(plan)" class="flex flex-col justify-start items-center py-3">
                  <div class="flex items-center justify-center gap-3">
                    <span :class="['pricing-price-value', getTabClass(plan.name)]">{{ plan.priceYearly }}</span>
                    <div v-if="plan.priceYearlyCurrency || plan.priceYearlySuffix" class="flex flex-col items-start">
                      <span v-if="plan.priceYearlyCurrency" class="pricing-currency">{{ plan.priceYearlyCurrency }}</span>
                      <span v-if="plan.priceYearlySuffix" class="pricing-suffix">{{ plan.priceYearlySuffix }}</span>
                    </div>
                  </div>
                  <p class="mb-4 text-xs lg:text-sm font-normal italic text-center align-middle text-[#5C636C]">
                    {{ getBillingText(plan) }}
                  </p>
                  <div class="h-8 min-h-8 flex items-center justify-center">
                    <p v-if="plan.yearlyNote" class="inline-block rounded-full px-4 py-1.5 text-xs lg:text-sm font-semibold text-center text-[#2C8A3C] bg-[#D5FAD8] leading-5">
                      {{ plan.yearlyNote }}
                    </p>
                  </div>
                </div>
                <div v-else class="flex flex-col justify-start items-center py-3"></div>

                <!-- Monthly Price (Secondary) -->
                <div v-if="hasMonthly(plan)" class="flex flex-col justify-start items-center mt-3 mb-7">
                  <div class="flex items-center justify-center gap-3">
                    <span class="text-[14px] lg:text-[28px] font-black leading-[120%] align-middle text-slate-700">{{ plan.priceMonthly }}</span>
                    <div v-if="plan.priceMonthlyCurrency || plan.priceMonthlySuffix" class="flex flex-col items-start">
                      <span v-if="plan.priceMonthlyCurrency" class="text-[8px] lg:text-xs font-black leading-[120%] align-middle text-[#B9BFC9]">{{ plan.priceMonthlyCurrency }}</span>
                      <span v-if="plan.priceMonthlySuffix" class="text-[8px] lg:text-xs font-black leading-[120%] align-middle text-[#B9BFC9]">{{ plan.priceMonthlySuffix }}</span>
                    </div>
                  </div>
                  <p class="mt-1 text-[10px] lg:text-sm font-normal italic text-center align-middle leading-[14px] lg:leading-normal text-[#5C636C]">Thanh toán theo tháng</p>
                </div>
                <div v-else class="flex flex-col justify-start items-center mt-3 mb-7"></div>
              </div>
            </div>

            <!-- Description -->
            <div class="h-14 min-h-14 flex items-center justify-center px-2">
              <p v-if="plan.description" class="text-xs lg:text-sm leading-relaxed text-slate-600 text-center">
                {{ plan.description }}
              </p>
            </div>

            <!-- Features -->
            <div class="flex-1 w-full px-8 mt-auto">
              <ul class="space-y-3">
                <li v-for="feature in plan.features" :key="feature" class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-[10px] h-[18px] md:w-3 md:h-5" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.0731 1.31716C8.97666 2.00536 6.3039 3.8458 3.83946 6.99844L2.3829 5.38204C2.15886 5.12608 1.74282 5.12608 1.51866 5.38204L0.446576 6.58252C0.238496 6.82252 0.254576 7.1746 0.478616 7.38268L3.77526 10.5513C4.0473 10.8074 4.49538 10.7433 4.6875 10.4073C6.4479 7.22248 8.33634 4.85404 11.5371 2.13328C11.9211 1.79728 11.569 1.15708 11.0729 1.31728L11.0731 1.31716Z"
                        fill="#2C8A3C"
                      />
                    </svg>
                  </div>
                  <span class="text-xs lg:text-sm text-slate-600 leading-relaxed">{{ feature }}</span>
                </li>
              </ul>
            </div>

            <!-- CTA Button -->
            <div class="w-full px-8">
              <a
                v-if="plan.cta_type !== 'secondary'"
                :href="primaryCtaUrl"
                :class="[
                  'group relative block w-full min-w-[100px] h-12 rounded-full py-2.5 px-5 text-center text-sm lg:text-lg font-bold align-middle transition-all duration-300 overflow-hidden shadow-[0_4px_4px_0_rgba(0,0,0,0.04)]',
                  plan.badge
                    ? 'bg-[linear-gradient(98.1deg,#892DDE_3.22%,#4A1878_100%),radial-gradient(59%_368.75%_at_52.5%_117.5%,rgba(232,15,239,0.2)_0%,rgba(255,255,255,0)_100%)] text-white hover:opacity-90'
                    : 'pricing-button-normal text-[#8326D9] bg-white hover:bg-slate-50',
                ]"
              >
                <span class="relative z-10">Bắt đầu ngay</span>
              </a>
              <button
                v-else
                type="button"
                class="group relative block w-full rounded-xl border border-slate-300 bg-white py-3 px-4 text-center text-xs lg:text-sm font-semibold text-slate-700 transition-all duration-300 hover:border-hilab/30 hover:bg-hilab/5 hover:text-hilab overflow-hidden"
                @click="handleSecondaryClick(plan)"
              >
                <span class="relative z-10">Liên hệ tư vấn</span>
                <div class="absolute inset-0 bg-gradient-to-r from-hilab/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
              </button>
            </div>
          </div>
        </article>
      </div>

      <!-- Enterprise Card - Horizontal Layout (PC only) -->
      <article v-if="enterprisePlan" class="hidden lg:block w-full mt-12">
        <div
          class="relative z-[1] w-full pt-[52px] pb-6 px-6 lg:px-10 rounded-2xl bg-[linear-gradient(103.39deg,#F6F7FF_1.17%,#FFFCFC_100%)] shadow-[0_4px_4px_rgba(0,0,0,0.02)] border border-[rgba(229,229,229,0.8)] border-b-4 border-b-[#E5E5E5] transition-all duration-200 scroll-scale-up hover:-translate-y-1 hover:shadow-lg"
        >
          <!-- Card Header Tab - Right aligned -->
          <div class="pricing-card-tab pricing-card-tab-right tab-enterprise">
            <h3 class="font-inter text-sm font-bold text-white uppercase tracking-wide">
              {{ enterprisePlan.name }}
            </h3>
          </div>

          <!-- 2 Column Layout -->
          <div class="flex flex-col lg:flex-row gap-6">
            <!-- Left Column: Info -->
            <div class="flex flex-col items-start lg:min-w-[200px]">
              <!-- Subtitle -->
              <p v-if="enterprisePlan.subtitle" class="text-xs lg:text-sm font-bold leading-5 text-[#64748B] mb-6">
                {{ enterprisePlan.subtitle }}
              </p>

              <!-- Pricing -->
              <div class="flex items-center gap-3 mb-3">
                <span class="pricing-price-value tab-enterprise">{{ enterprisePlan.priceYearly }}</span>
              </div>

              <!-- Billing Text -->
              <p class="text-xs lg:text-sm font-normal italic text-[#5C636C] mb-[22px]">
                {{ getBillingText(enterprisePlan) }}
              </p>

              <!-- CTA Link Button -->
              <button
                type="button"
                class="inline-flex items-center gap-1.5 text-xs lg:text-sm font-semibold text-[#36393E] transition-all duration-200 hover:underline"
                @click="handleSecondaryClick(enterprisePlan)"
              >
                <span>LIÊN HỆ TƯ VẤN NGAY</span>
                <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="rotate-[-45deg]">
                  <path d="M6 1L11 7L6 13M11 7H1" stroke="#242629" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </div>

            <!-- Right Column: Features -->
            <div class="flex-1 px-0 lg:px-6">
              <ul class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                <li v-for="feature in enterprisePlan.features" :key="feature" class="flex items-start gap-3">
                  <div class="flex-shrink-0 mt-0.5">
                    <svg class="w-[10px] h-[18px] md:w-3 md:h-5" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.0731 1.31716C8.97666 2.00536 6.3039 3.8458 3.83946 6.99844L2.3829 5.38204C2.15886 5.12608 1.74282 5.12608 1.51866 5.38204L0.446576 6.58252C0.238496 6.82252 0.254576 7.1746 0.478616 7.38268L3.77526 10.5513C4.0473 10.8074 4.49538 10.7433 4.6875 10.4073C6.4479 7.22248 8.33634 4.85404 11.5371 2.13328C11.9211 1.79728 11.569 1.15708 11.0729 1.31728L11.0731 1.31716Z"
                        fill="#2C8A3C"
                      />
                    </svg>
                  </div>
                  <span class="text-xs lg:text-sm text-slate-600 leading-relaxed">{{ feature }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </article>
      <div v-if="cta" class="mt-12 rounded-3xl border border-hilab/20 bg-white/80 p-8 text-sm text-slate-500 shadow-lg">
        <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p v-if="cta.title" class="text-lg font-semibold text-slate-900">
              {{ cta.title }}
            </p>
            <p v-if="cta.description" class="mt-2 text-sm text-slate-500">
              {{ cta.description }}
            </p>
          </div>
          <a v-if="cta.label" :href="cta.url || primaryCtaUrl" class="primary-button">{{ cta.label }}</a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';

const emit = defineEmits(['request-consultation']);

const props = defineProps({
  primaryCtaUrl: {
    type: String,
    required: true,
  },
  content: {
    type: Object,
    default: () => ({}),
  },
});

const sectionContent = computed(() => props.content || {});
const badge = computed(() => sectionContent.value.badge || '');
const title = computed(() => sectionContent.value.title || '');
const heroDescription = computed(() => sectionContent.value.description || '');
const rawPlans = computed(() => (Array.isArray(sectionContent.value.plans) ? sectionContent.value.plans : []));
const normalizeType = t => (typeof t === 'string' ? t.trim().toLowerCase() : '');

const pricingPlans = computed(() =>
  rawPlans.value
    .map(plan => ({
      name: plan.name,
      subtitle: plan.subtitle,
      badge: plan.badge,
      priceYearlyCurrency: plan.price_yearly_currency,
      priceYearly: plan.price_yearly,
      priceYearlySuffix: plan.price_yearly_suffix,
      priceMonthlyCurrency: plan.price_monthly_currency,
      priceMonthly: plan.price_monthly,
      priceMonthlySuffix: plan.price_monthly_suffix,
      yearlyNote: plan.yearly_note,
      description: plan.description,
      features: plan.features || [],
      cta_type: normalizeType(plan.cta_type),
      cta_url: plan.cta_url,
    }))
    .filter(plan => plan.name)
);

// Separate enterprise plan (last plan with name "enterprise")
const isEnterprise = plan => plan.name?.toLowerCase().includes('enterprise');

// For mobile/tablet: all plans including Enterprise (shown in grid)
const allPlans = computed(() => pricingPlans.value);

// For PC: regular plans only (Enterprise excluded from grid)
const regularPlans = computed(() => {
  return pricingPlans.value.filter(plan => !isEnterprise(plan));
});

const enterprisePlan = computed(() => {
  const plans = pricingPlans.value;
  const lastPlan = plans[plans.length - 1];
  if (lastPlan && isEnterprise(lastPlan)) {
    return lastPlan;
  }
  return null;
});

const cta = computed(() => {
  const data = sectionContent.value.cta;
  if (!data) {
    return null;
  }
  if (data.title || data.description || data.label) {
    return data;
  }
  return null;
});

// Helpers
const hasYearly = plan => Boolean(plan.priceYearly);
const hasMonthly = plan => Boolean(plan.priceMonthly);

const getTabClass = planName => {
  const name = planName?.toLowerCase() || '';
  if (name.includes('basic')) return 'tab-basic';
  if (name.includes('growth')) return 'tab-growth';
  if (name.includes('enterprise')) return 'tab-enterprise';
  return ''; // Starter (default)
};

const getBillingText = plan => {
  const priceYearly = plan?.priceYearly?.toLowerCase() || '';
  const priceYearlyValue = plan?.priceYearly || '';

  if (priceYearly.includes('miễn phí') || priceYearly.includes('free') || priceYearlyValue === '0' || !priceYearlyValue) {
    return 'Sau đó thanh toán theo gói sử dụng';
  }

  return 'Thanh toán theo năm';
};

const handleSecondaryClick = plan => {
  const planName = plan?.name ? plan.name : '';
  emit('request-consultation', planName ? `pricing:${planName}` : 'pricing');
};
</script>
